import type { SelfServePlanKey } from "@giftforward/entitlements";
import type { AnalyticsPlan, SignUpMethod } from "@/interfaces/analytics";

const PENDING_SIGN_UP_KEY = "gfg_signup_pending";

interface PendingSignUp {
  clerkUserId: string;
  method: SignUpMethod;
  plan: AnalyticsPlan;
}

/** GTM reads every push; the event and key names are configured in GTM, so they must not change. */
export function pushDataLayer(entry: Record<string, unknown>): void {
  const w = window as typeof window & { dataLayer?: unknown[] };
  w.dataLayer = w.dataLayer ?? [];
  w.dataLayer.push(entry);
}

/** Router state for a `replace: true` navigation that is a real page change, not a redirect. */
export const PAGE_VIEW_STATE = { pageView: true } as const;

export function isPageViewState(state: unknown): boolean {
  return (state as { pageView?: unknown } | null)?.pageView === true;
}

/** The `/g/:token` segment is a redemption credential and must never reach a cookie or GA4. */
export function redactTokenPath(path: string): string {
  return path.replace(/^\/g\/[^/?#]+/, "/g/:token");
}

export function pushVirtualPageView(pagePath: string): void {
  pushDataLayer({ event: "virtual_page_view", page_path: redactTokenPath(pagePath) });
}

/** The internal numeric user id — never the email or the Clerk id. */
export function setAnalyticsUser(userId: number): void {
  pushDataLayer({ user_id: String(userId) });
}

export function clearAnalyticsUser(): void {
  pushDataLayer({ user_id: undefined });
}

export function pushSignUpStart(method: SignUpMethod): void {
  pushDataLayer({ event: "app_signup_start", method, plan_intent: "starter" });
}

export function pushSignUp(method: SignUpMethod, plan: AnalyticsPlan): void {
  pushDataLayer({ event: "sign_up", method, plan });
}

export function toAnalyticsPlan(plan: "free" | SelfServePlanKey): AnalyticsPlan {
  return plan === "free" ? "starter" : plan;
}

export function signUpMethodFor(user: { externalAccounts: { provider: string }[] }): SignUpMethod {
  const providers = user.externalAccounts.map((account) => account.provider);
  if (providers.some((p) => p.includes("google"))) return "google_sso";
  if (providers.some((p) => p.includes("microsoft"))) return "microsoft_sso";
  return "email";
}

/**
 * sign_up is held back until the plan settles: recorded when the organization is created, its plan
 * raised if the onboarding payment succeeds, and pushed once on the first app page after that.
 */
export function markSignUpPending(clerkUserId: string, method: SignUpMethod): void {
  writePendingSignUp({ clerkUserId, method, plan: "starter" });
}

export function updatePendingSignUpPlan(plan: AnalyticsPlan): void {
  const pending = readPendingSignUp();
  if (pending) writePendingSignUp({ ...pending, plan });
}

/** Returns the pending sign_up for this user and removes it, so a refresh never pushes it twice. */
export function consumePendingSignUp(clerkUserId: string): PendingSignUp | null {
  const pending = readPendingSignUp();
  if (!pending || pending.clerkUserId !== clerkUserId) return null;
  try {
    localStorage.removeItem(PENDING_SIGN_UP_KEY);
  } catch {
    return null;
  }
  return pending;
}

function readPendingSignUp(): PendingSignUp | null {
  try {
    const raw = localStorage.getItem(PENDING_SIGN_UP_KEY);
    return raw ? (JSON.parse(raw) as PendingSignUp) : null;
  } catch {
    return null;
  }
}

function writePendingSignUp(pending: PendingSignUp): void {
  try {
    localStorage.setItem(PENDING_SIGN_UP_KEY, JSON.stringify(pending));
  } catch {
    // Storage unavailable: this sign_up is lost, which must never block onboarding.
  }
}
